import { readableColor, transparentize } from 'polished';
import styled from 'styled-components';
import { LinkHoverCss } from '@/components/uielements/css';
import { COLORS, LAYOUT } from '@/constants/styles';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import { safePolished } from '@/utils/safePolished';

export const StyledRewardsFlexContainer = styled.div`
  display: flex;
  max-width: ${LAYOUT.MAX_LOYALTY_CONTAINER_WIDTH}px;
  flex-wrap: wrap;
  padding: ${LAYOUT.GUTTER}px;
  width: 100%;
`;

interface StyledRewardProps {
  $inactive?: boolean;
}

export const StyledReward = styled.div<StyledRewardProps>`
  ${({ theme }) => `
    background: ${theme.location_card.background_color};
    border-radius: ${theme.location_card.border_radius}px;
    border: 1px solid ${theme.location_card.border_color};
  `}

  ${({ $inactive }) =>
    $inactive &&
    `
    opacity: 0.5;
    pointer-events: none;
  `}

  display: flex;
  gap: var(--space-4);
  flex-direction: column;
  justify-content: space-between;
  width: 32%;
  margin: 0 2% 2% 0;
  padding: ${LAYOUT.GUTTER}px;

  &:nth-child(3n + 3) {
    margin-right: 0;
  }

  @media (max-width: 1024px) {
    width: 49%;
    margin-right: 2%;

    &:nth-child(3n + 3) {
      margin-right: 2%;
    }

    &:nth-of-type(even) {
      margin-right: 0;
    }
  }

  @media (max-width: 767px) {
    &:nth-of-type(even) {
      margin: 0 0 ${LAYOUT.GUTTER}px 0;
    }

    &:nth-of-type(odd) {
      margin: 0 0 ${LAYOUT.GUTTER}px 0;
    }

    width: 100%;
  }
`;

export const StyledRewardTopContent = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  '> div:nth-child(2)': {
    padding: '0 20px',
  },

  h3: {
    margin: '0 0 5px 0',
    ...getFontStyles(theme.location_card.secondary_font),
  },

  h4: {
    margin: 0,
    ...getFontStyles(theme.location_card.font, {
      color: () => theme.global.primary_active_color,
    }),
  },

  p: {
    margin: 0,
    ...getFontStyles(theme.location_card.font, {
      color: (value) => safePolished(transparentize)('0.2', value),
    }),
  },

  img: {
    maxHeight: '100px',
    maxWidth: '125px',
  },
}));

interface StyledRewardImageProps {
  $inactive: boolean;
}

export const StyledRewardImage = styled.div<StyledRewardImageProps>`
  position: relative;

  ${({ $inactive }) =>
    $inactive &&
    `
    img {
      filter: blur(4px);
    }

    &:after {
      content: url(/static/img/icons/lock.png);
      left: calc(50% - 12px);
      position: absolute;
      top: calc(50% - 12px);
    }
  `}
`;

export const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--size-2);
  width: 100%;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

export const StyledRewardDescription = styled.p`
  margin: 0;
`;

export const StyledRewardDisclaimer = styled.p(({ theme }) => ({
  fontSize: '12px',
  ...getFontStyles(theme.text.primary_font, ['color'], {
    color: (value) => (value ? safePolished(transparentize)('0.1', value) : 'inherit'),
  }),
}));

export const StyledRewardsHeaderContainer = styled.div`
  padding: ${LAYOUT.GUTTER}px ${LAYOUT.GUTTER}px 0;

  p {
    margin: 0 0 ${LAYOUT.GUTTER}px;
  }
`;

const deriveRewardsFooterAlignment = (config: string) => {
  switch (config) {
    case 'center':
      return `center`;
    case 'right':
      return `flex-end`;
    default:
      return `flex-start`;
  }
};

export const StyledRewardsFooterBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  gap: var(--size-4);
  align-items: ${({ theme }) =>
    deriveRewardsFooterAlignment(theme.rewards.footer_heading_text_align)};

  h1 {
    text-align: ${({ theme }) => theme.rewards.footer_heading_text_align} !important;
  }

  button {
    min-width: 75%;
  }
`;

export const StyledRewardsButtonHeader = styled.h2`
  line-height: 1.1;
  margin: 0;
  font-size: var(--config-text__secondary-text-size);
  text-align: ${({ theme }) => theme.rewards.footer_heading_text_align} !important;
`;

export const StyledLinkButton = styled.button`
  background: none;
  border: none;
  font-family: ${({ theme }) => theme.primary_font.bold.font_family};
  font-size: 16px;
  cursor: pointer;

  ${LinkHoverCss}
`;

export const StyledMembershipLevelTag = styled.div(({ theme }) => {
  const primaryFont = getFontStyles(theme.text.primary_font);
  return {
    alignItems: 'center',
    background: primaryFont.color,
    borderRadius: '20px',
    color: safePolished(readableColor)(primaryFont.color),
    display: 'flex',
    fontFamily: theme.primary_font.bold.font_family,
    fontSize: '14px',
    height: '40px',
    justifyContent: 'center',
    margin: '0 auto',
    maxWidth: '250px',

    img: {
      height: '16px',
      marginRight: '10px',
    },
  };
});

export const StyledNoOffers = styled.div`
  justify-content: center;
  text-align: center;
  margin: 0 auto;
  padding: ${LAYOUT.GUTTER * 2}px ${LAYOUT.GUTTER}px;

  h3 {
    font-size: 20px;
    font-family: ${({ theme }) => theme.primary_font.bold.font_family};
  }

  p {
    font-size: 16px;
  }

  img {
    height: 80px;
  }
`;

export const StyledRewardsHeader = styled.h1`
  font-size: 24px;
  font-family: ${({ theme }) => theme.primary_font.bold.font_family};
  margin: 0;
`;

export const StyledBorderBottom = styled.div`
  border-bottom: solid 1px ${({ theme }) => theme.global.primary_border_color};
`;

export const StyledBankedCash = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  max-width: 560px;
  border-radius: 8px;
  box-shadow: 2px 2px 16px 0 #ccc;
  text-align: center;
  background: ${COLORS.WHITE};
  margin: ${LAYOUT.GUTTER * 2}px auto;
  padding: ${LAYOUT.GUTTER}px;
  max-height: 100%;
  gap: var(--size-4);

  @media (max-width: 767px) {
    margin: ${LAYOUT.GUTTER}px;
  }
`;

export const StyledBankedCashContent = styled.div`
  padding: ${LAYOUT.GUTTER}px;
  padding-bottom: 0;

  h3 {
    font-family: ${({ theme }) => theme.primary_font.bold.font_family};
    font-size: 16px;
    margin: 0;
  }

  h1 {
    font-family: ${({ theme }) => theme.primary_font.bold.font_family};
    font-size: 72px;
    margin: 0;

    @media (max-width: 767px) {
      font-size: 54px;
    }
  }

  p {
    color: ${({ theme }) => safePolished(transparentize)('0.1', theme.text.primary_font.color)};
  }
`;

export const StyledCashButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  max-width: 100%;

  @media (max-width: 767px) {
    flex-direction: column;
  }

  button {
    width: 50%;

    @media (max-width: 767px) {
      width: 100%;
    }

    &:first-child {
      margin: 0 ${LAYOUT.GUTTER / 5}px 0 0;

      @media (max-width: 767px) {
        margin: 0 0 ${LAYOUT.GUTTER / 5}px 0;
      }
    }

    &:last-child {
      margin: 0 0 0 ${LAYOUT.GUTTER / 5}px;

      @media (max-width: 767px) {
        margin: ${LAYOUT.GUTTER / 5}px 0 0 0;
      }
    }
  }
`;
